// These helpers are calling this template's own server-side routes
// so, they are not directly calling Marketplace API or Integration API.
// You can find these api endpoints from 'server/api/...' directory

import appSettings from '../config/settings';
import { types as sdkTypes, transit } from './sdkLoader';
import Decimal from 'decimal.js';
import axios from 'axios';

export const apiBaseUrl = marketplaceRootURL => {
  const port = process.env.REACT_APP_DEV_API_SERVER_PORT;
  const useDevApiServer = process.env.NODE_ENV === 'development' && !!port;

  // In development, the dev API server is running in a different port
  if (useDevApiServer) {
    return `http://localhost:${port}`;
  }

  // Otherwise, use the given marketplaceRootURL parameter or the same domain and port as the frontend
  return marketplaceRootURL ? marketplaceRootURL.replace(/\/$/, '') : `${window.location.origin}`;
};

// Application type handlers for JS SDK.
//
// NOTE: keep in sync with `typeHandlers` in `server/api-util/sdk.js`
export const typeHandlers = [
  // Use Decimal type instead of SDK's BigDecimal.
  {
    type: sdkTypes.BigDecimal,
    customType: Decimal,
    writer: v => new sdkTypes.BigDecimal(v.toString()),
    reader: v => new Decimal(v.value),
  },
];

const serialize = data => {
  return transit.write(data, { typeHandlers, verbose: appSettings.sdk.transitVerbose });
};

const deserialize = str => {
  return transit.read(str, { typeHandlers });
};

const methods = {
  POST: 'POST',
  GET: 'GET',
  PUT: 'PUT',
  PATCH: 'PATCH',
  DELETE: 'DELETE',
};

// If server/api returns data from SDK, you should set Content-Type to 'application/transit+json'
const request = (path, options = {}) => {
  const url = `${apiBaseUrl()}${path}`;
  const { credentials, headers, body, ...rest } = options;

  // If headers are not set, we assume that the body should be serialized as transit format.
  const shouldSerializeBody =
    (!headers || headers['Content-Type'] === 'application/transit+json') && body;
  const bodyMaybe = shouldSerializeBody ? { body: serialize(body) } : {};

  const fetchOptions = {
    credentials: credentials || 'include',
    // Since server/api mostly talks to Marketplace API using SDK,
    // we default to 'application/transit+json' as content type (as SDK uses transit).
    headers: headers || { 'Content-Type': 'application/transit+json' },
    ...bodyMaybe,
    ...rest,
  };

  return window.fetch(url, fetchOptions).then(res => {
    const contentTypeHeader = res.headers.get('Content-Type');
    const contentType = contentTypeHeader ? contentTypeHeader.split(';')[0] : null;

    if (res.status >= 400) {
      return res.json().then(data => {
        let e = new Error();
        e = Object.assign(e, data);

        throw e;
      });
    }
    if (contentType === 'application/transit+json') {
      return res.text().then(deserialize);
    } else if (contentType === 'application/json') {
      return res.json();
    }
    return res.text();
  });
};

// Keep the previous parameter order for the post method.
// For now, only POST has own specific function, but you can create more or use request directly.
const post = (path, body, options = {}) => {
  const requestOptions = {
    ...options,
    method: methods.POST,
    body,
  };

  return request(path, requestOptions);
};

const get = (path, options) => {
  const reqOptions = {
    ...options,
    method: methods.GET,
  };

  return request(path, reqOptions);
};

// Fetch transaction line items from the local API endpoint.
//
// See `server/api/transaction-line-items.js` to see what data should
// be sent in the body.
export const transactionLineItems = body => {
  return post('/api/transaction-line-items', body);
};

// Initiate a privileged transaction.
//
// With privileged transitions, the transactions need to be created
// from the backend. This endpoint enables sending the order data to
// the local backend, and passing that to the Marketplace API.
//
// See `server/api/initiate-privileged.js` to see what data should be
// sent in the body.
export const initiatePrivileged = body => {
  return post('/api/initiate-privileged', body);
};

// Transition a transaction with a privileged transition.
//
// This is similar to the `initiatePrivileged` above. It will use the
// backend for the transition. The backend endpoint will add the
// payment line items to the transition params.
//
// See `server/api/transition-privileged.js` to see what data should
// be sent in the body.
export const transitionPrivileged = body => {
  return post('/api/transition-privileged', body);
};

// Create user with identity provider (e.g. Facebook or Google)
//
// If loginWithIdp api call fails and user can't authenticate to Marketplace API with idp
// we will show option to create a new user with idp.
// For that user needs to confirm data fetched from the idp.
// After the confirmation, this endpoint is called to create a new user with confirmed data.
//
// See `server/api/auth/createUserWithIdp.js` to see what data should
// be sent in the body.
export const createUserWithIdp = body => {
  return post('/api/auth/create-user-with-idp', body);
};

// Fileuploader

export const uploadFiles = async data => {
  const options = {
    baseURL: apiBaseUrl(),
    method: 'post',
    url: `${apiBaseUrl()}/api/files/upload`,
    headers: {
      'Content-Type': 'application/x-www-urlencoded',
    },
    data,
  };

  return axios(options);
};

export const deleteFile = async (key, id, entity, mediaName) => {
  const options = {
    baseURL: apiBaseUrl(),
    method: 'delete',
    url: `${apiBaseUrl()}/api/files/delete?key=${key}&id=${id}&entity=${entity}&mediaName=${mediaName}`,
    headers: {
      'Content-Type': 'application/json',
    },
  };

  return axios(options);
};
export const cancelAndPartialRefund = body => {
  return post('/api/cancel-and-partial-refund', body);
};

export const sendWebHookOnDispute = async body => {
  return post('/api/webhook/send-payload', body);
};

// Brevo newsletter subscription
export const brevoSubscription = body => {
  return post('/api/brevo-subscription', body);
};

// Brevo SMS
export const brevoSendSms = body => {
  return post('/api/brevo-sms', body);
};

export const sendMail = body => {
  return post('/api/emails/contact-us', body);
};

export const getAthletesByParams = value => {
  return post('/api/athletes', value);
};

export const updateUserById = value => {
  return post('/api/update-user-by-id', value);
};

export const updateTransactionMetadata = body => {
  return post('/api/transactions/update-transaction-metadata', body);
};

export const updateListingData = body => {
  return post('/api/transactions/update-listing-data', body);
};

export const updateCustomSlug = body => {
  return post('/api/slug', body);
};

export const checkCustomSlug = body => {
  return post('/api/slug/check', body);
};

export const fetchUserIdByCustomSlug = customSlug => {
  return get(`/api/slug/user/${customSlug}`);
};

export const fetchCustomSlugByUserId = userId => {
  return get(`/api/slug/slug/${userId}`);
};

export const getUserByEmail = body => {
  return post('/api/get-user-by-email', body);
};

export const getUserById = body => {
  return post('/api/get-user-by-id', body);
};

// Shoreio link shortening
export const shortenLink = body => {
  return post('/api/short-link', body);
};
